// src/pages/expense/_shared/ExpenseItemPanel.jsx
// ✅ 거래처/품목 DB 연동 공통화 — 환경/종합 파일럿(env/total/ExpenseItemPanel.jsx)에만
// 있던 "품목명/내용명 DB 입력" 새 창 버튼 + 사용내역 내용명(ExpenseItem 1000~1999)
// 자동완성을 8개 사업(환경/종합·환경/소독·경영팀·용역사업 — 카페 4개점은
// itemPanelTemplates가 비어있어 이 패널 자체를 쓰지 않음) 공용 컴포넌트로 승격.
// 각 사업 폴더에 남아있던 구버전 ExpenseItemPanel.jsx는 (구) 단독 페이지
// (ExpenseBusinessNew.jsx 등)가 계속 쓰므로 그대로 두고 손대지 않는다.
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./ExpenseItemPanel.module.css";

// ✅ 데이터 행 인라인 편집 입력 — 입력행(.inputRow)만큼 셀을 꽉 채우진 않지만
// 최소한의 시각적 일관성(중앙 정렬, 작은 테두리)만 준다.
const INLINE_EDIT_STYLE = {
  width: "100%",
  textAlign: "center",
  fontSize: 13,
  border: "1px solid #e5e7eb",
  borderRadius: 4,
  padding: "4px 2px",
  boxSizing: "border-box",
};

// ✅ initialItems/initialForm/autoUnitRules — 유류비(세차비) 전용 편의 기능.
// 다른 템플릿은 셋 다 안 넘기므로(undefined) 기존 동작과 완전히 동일하다.
// - initialItems: 진입 시 품목 표에 미리 채워둘 행들(예: 유류비/세차비 2행,
//   수량·단가는 빈칸). 아래 표의 수량·단가·단위는 항상 인라인 편집 가능하므로
//   사용자가 그대로 채워넣을 수 있다(부모가 key prop을 바꿔야 재적용됨).
// - initialForm: 입력행(새로 추가할 행) 최초 기본값.
// - autoUnitRules: [{match, unit}] — 품목명에 match 문자열이 포함되면 단위를
//   자동으로 unit으로 바꾼다(예: "세차" 포함 시 "식"). 이후에도 자유 수정 가능.
export default function ExpenseItemPanel({ itemCodes = [], onItemsChange, initialItems, initialForm, autoUnitRules }) {
  const [items, setItems] = useState(() => initialItems || []);
  const [form, setForm] = useState({
    name: "",
    unit: "",
    qty: "",
    price: "",
    amount: "",
    supply: "",
    vat: "",
    remark: "",
    ...(initialForm || {}),
  });

  // ✅ initialItems로 미리 채운 행은 부모(itemList) 상태에도 마운트 시 한 번
  // 동기화해야 한다 — 안 하면 부모는 빈 배열로 알고 있어 합계/세부내역 등이
  // 화면(자식 표)과 어긋난다.
  useEffect(() => {
    if (initialItems && initialItems.length) onItemsChange && onItemsChange(initialItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ✅ 금액(자동) 셀을 사용자가 직접 수정했는지 — 수정 시 수량×단가 자동계산보다
  // 우선하고(영수증 실금액 1원 단위 보정용), 배경도 회색(자동)→흰색(수동)으로 전환.
  const [amountTouched, setAmountTouched] = useState(false);

  // ✅ 품목명 자동완성(사용내역 내용명, ExpenseItem 1000~1999) — 거래처 검색과 동일한
  // UX: 타이핑 → 부분일치 목록 → 선택/키보드 탐색, 목록에 없으면 자유 입력 유지.
  const [showNameSuggest, setShowNameSuggest] = useState(false);
  const [nameHighlight, setNameHighlight] = useState(-1);
  const nameSuggestRef = useRef(null);
  const filteredNameCodes = itemCodes.filter((c) =>
    form.name.trim() && c.name.toLowerCase().includes(form.name.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (nameSuggestRef.current && !nameSuggestRef.current.contains(e.target)) {
        setShowNameSuggest(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ 자동완성 드롭다운을 document.body에 portal로 렌더 + position:fixed로 좌표를
  // 직접 계산한다 — .sidePane이 overflow-x:hidden이라 CSS 스펙상 overflow-y:visible이
  // 암묵적으로 auto로 강제 계산돼(한쪽 축이 visible이 아니면 반대 축도 auto), 품목
  // 표가 짧을 때(입력행+합계행뿐 등) 드롭다운이 .sidePane 바닥 아래에서 잘리고 이후에
  // 오는 붙임 패널 밑에 깔려 보이는 문제가 있었다(VendorPanel.jsx와 동일한 방식으로
  // 해결). 위치는 nameInputRef의 getBoundingClientRect() 기준, 스크롤/리사이즈 시 재계산.
  const nameInputRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  useEffect(() => {
    if (!showNameSuggest) {
      setDropdownRect(null);
      return;
    }
    const update = () => {
      const el = nameInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [showNameSuggest]);

  const selectNameSuggestion = (c) => {
    setForm((prev) => ({ ...prev, name: c.name }));
    setShowNameSuggest(false);
  };

  const handleNameKeyDown = (e) => {
    if (!showNameSuggest || !filteredNameCodes.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNameHighlight((i) => (i < filteredNameCodes.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNameHighlight((i) => (i > 0 ? i - 1 : filteredNameCodes.length - 1));
    } else if (e.key === "Enter") {
      if (nameHighlight >= 0) {
        e.preventDefault();
        selectNameSuggestion(filteredNameCodes[nameHighlight]);
      }
    } else if (e.key === "Escape") {
      setShowNameSuggest(false);
    }
  };

  const comma = (n) =>
    n ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
  const uncomma = (n) => n.replace(/,/g, "");
  // ✅ 수량은 소수점 둘째 자리까지 — 정수 입력 시 41.00이 아니라 41로 표시(끝자리
  // 0 제거는 parseFloat().toString()이 자연히 처리).
  const cleanQty = (n) => {
    const v = parseFloat(uncomma(n || "0"));
    return isNaN(v) ? 0 : v;
  };
  // 소수점 입력 중(예: "41.")에도 지우지 않도록 원문 문자열은 그대로 두고, 숫자가
  // 아닌 문자만 제거 + 소수점 둘째 자리까지만 허용.
  const sanitizeQtyInput = (value) => {
    let v = value.replace(/,/g, "").replace(/[^\d.]/g, "");
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
    }
    return v;
  };

  // 공급가액, 부가세 계산 코드 부가세를 먼저 계산 한 후 반올림 후 공급가액 계산
  const calc = (qty, price) => {
    // ✅ 수량이 소수(예: 41.44)면 qty*price가 원 단위 소수가 되므로 먼저 반올림 —
    // 금액/공급가액/부가세는 항상 원 단위 정수.
    const total = Math.round(qty * price);
    const vat = Math.round(total / 11);  // 부가세 10% = 총액의 1/11
    const supply = total - vat;
    return { total, supply, vat };
  };

  // ✅ 금액을 직접 수정한 경우 — 공급가액/부가세를 "금액 기준"으로 재계산(영수증
  // 실금액 1원 단위 보정용). 공급가액 = 금액/1.1 반올림, 부가세 = 금액 - 공급가액.
  const calcFromAmount = (amount) => {
    const supply = Math.round(amount / 1.1);
    const vat = amount - supply;
    return { total: amount, supply, vat };
  };

  // ✅ 수량이나 단가가 변경되면 자동으로 금액/공급가액/부가세 계산 — 단, 금액을
  // 사용자가 직접 수정했으면(amountTouched) 자동계산을 덮어쓰지 않는다.
  useEffect(() => {
    if (amountTouched) return;
    const qty = cleanQty(form.qty);
    const price = parseInt(uncomma(form.price)) || 0;

    if (qty > 0 && price > 0) {
      const calculated = calc(qty, price);
      setForm(prev => ({
        ...prev,
        amount: comma(calculated.total.toString()),
        supply: comma(calculated.supply.toString()),
        vat: comma(calculated.vat.toString())
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.qty, form.price, amountTouched]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      setShowNameSuggest(value.trim().length > 0);
      setNameHighlight(-1);
      const rule = (autoUnitRules || []).find((r) => value.includes(r.match));
      setForm((prev) => ({ ...prev, name: value, ...(rule ? { unit: rule.unit } : {}) }));
      return;
    }

    if (name === "amount") {
      setAmountTouched(true);
      const cleaned = value.replace(/,/g, "").replace(/[^\d]/g, "");
      const amount = parseInt(cleaned) || 0;
      const { supply, vat } = calcFromAmount(amount);
      setForm(prev => ({
        ...prev,
        amount: comma(cleaned),
        supply: comma(supply.toString()),
        vat: comma(vat.toString()),
      }));
      return;
    }

    if (name === "qty") {
      setForm({ ...form, qty: sanitizeQtyInput(value) });
      return;
    }

    // 숫자 필드는 쉼표 처리
    if (['price', 'supply', 'vat'].includes(name)) {
      setForm({
        ...form,
        [name]: comma(value.replace(/,/g, "")),
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.name === "remark") {
      e.preventDefault();
      addItem();
    }
  };

  const addItem = () => {
    const qty = Math.round(parseFloat(uncomma(form.qty)) * 100) / 100;
    const price = parseInt(uncomma(form.price));
    if (!form.name) return alert("품목명을 입력하세요");
    if (isNaN(qty) || isNaN(price)) return alert("수량/단가 숫자 입력");

    let supply = parseInt(uncomma(form.supply));
    let vat = parseInt(uncomma(form.vat));

    // 공급가액이나 부가세가 비어있으면 자동 계산
    if (isNaN(supply) || isNaN(vat)) {
      const t = calc(qty, price);
      supply = t.supply;
      vat = t.vat;
    }

    const newItem = {
      name: form.name,
      unit: form.unit,
      remark: form.remark,
      qty,
      price,
      supply,
      vat,
      total: supply + vat,
    };

    setItems([...items, newItem]);
    onItemsChange && onItemsChange([...items, newItem]);

    setAmountTouched(false);
    setForm({
      name: "",
      unit: "",
      qty: "",
      price: "",
      amount: "",
      supply: "",
      vat: "",
      remark: "",
    });
  };

  const removeItem = (idx) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    const updated = items.filter((_, i) => i !== idx);
    setItems(updated);
    onItemsChange && onItemsChange(updated);
  };

  // ✅ 이미 추가된 행의 단위/수량/단가를 표에서 바로 고칠 수 있게 하는 인라인
  // 편집(유류비(세차비) 기본 2행처럼 수량·단가가 빈칸으로 시작하는 행을 사용자가
  // 채워야 하는 경우를 위해 신설). 수량·단가가 둘 다 채워지면 공급가액/부가세/
  // 금액을 자동 재계산, 아니면 0으로 둔다(빈 행은 저장 시 부모 쪽에서 제외).
  //
  // ✅ 공급가액/부가세 수동 보정 — 입력행의 amountTouched와 같은 원리를 행별로
  // 확장(supplyTouched/vatTouched, item 객체에 직접 보관). 한쪽만 수정하면
  // 금액(total, 고정 기준값)에서 나머지를 자동 보완해 항상 합이 맞게 유지하고,
  // 양쪽 다 수정되면(둘 다 touched) 더는 자동 보완하지 않는다 — 그래야 의도적
  // 불일치(면세 특이 케이스 등)도 표현 가능. qty/price가 바뀌어 금액이 다시
  // 계산될 때도 이미 touched된 쪽은 덮어쓰지 않고, 한쪽만 touched면 나머지 한쪽만
  // 새 금액 기준으로 재보완한다(둘 다 touched면 금액만 갱신, 불일치 유지).
  const updateItemField = (idx, field, rawValue) => {
    const it = { ...items[idx] };

    if (field === "supply") {
      it.supply = parseInt(uncomma(rawValue).replace(/[^\d]/g, "")) || 0;
      it.supplyTouched = true;
      if (!it.vatTouched) it.vat = it.total - it.supply;
      const next = items.map((x, i) => (i === idx ? it : x));
      setItems(next);
      onItemsChange && onItemsChange(next);
      return;
    }
    if (field === "vat") {
      it.vat = parseInt(uncomma(rawValue).replace(/[^\d]/g, "")) || 0;
      it.vatTouched = true;
      if (!it.supplyTouched) it.supply = it.total - it.vat;
      const next = items.map((x, i) => (i === idx ? it : x));
      setItems(next);
      onItemsChange && onItemsChange(next);
      return;
    }

    if (field === "unit") it.unit = rawValue;
    else if (field === "qty") it.qty = sanitizeQtyInput(rawValue.replace(/,/g, ""));
    else if (field === "price") it.price = uncomma(rawValue).replace(/[^\d]/g, "");

    const qtyNum = cleanQty(it.qty);
    const priceNum = parseInt(uncomma(String(it.price || "0"))) || 0;
    if (qtyNum > 0 && priceNum > 0) {
      const t = calc(qtyNum, priceNum);
      it.total = t.total;
      if (!it.supplyTouched && !it.vatTouched) {
        it.supply = t.supply;
        it.vat = t.vat;
      } else if (it.supplyTouched && !it.vatTouched) {
        it.vat = it.total - it.supply;
      } else if (!it.supplyTouched && it.vatTouched) {
        it.supply = it.total - it.vat;
      }
      // 둘 다 touched면 금액만 갱신하고 공급가액/부가세는 그대로 둔다(불일치 가능).
    } else {
      it.supply = 0;
      it.vat = 0;
      it.total = 0;
      it.supplyTouched = false;
      it.vatTouched = false;
    }

    const next = items.map((x, i) => (i === idx ? it : x));
    setItems(next);
    onItemsChange && onItemsChange(next);
  };

  const sumSupply = items.reduce((a, b) => a + b.supply, 0);
  const sumVat = items.reduce((a, b) => a + b.vat, 0);
  const sumTotal = items.reduce((a, b) => a + b.total, 0);

  return (
    <div className={styles.sidePane}>

      {/* ✅ 입력 + 데이터 통합 테이블 */}
      <div className={styles.scrollWrap}>
        <table className={styles.itemTable}>
          <thead>
            <tr>
              <th>
                품목명/내용명
                {/* ✅ 새 창으로 지출 사용내역 코드 관리(/etc/expense-usage-codes)를
                    열어 즉시 내용명(1001~1999) 등록 — 거래처 DB 입력 버튼과 동일한
                    팝업 규격·창 포커스 복귀 재조회 방식(부모(ExpenseDraftNew)의 focus
                    리스너가 itemCodes도 함께 재조회). */}
                <button
                  type="button"
                  onClick={() => window.open("/etc/expense-usage-codes", "expenseUsageCodesPopup", "width=1000,height=800")}
                  title="새 창에서 사용내역 내용명 등록"
                  className={styles.headerDbButton}
                >
                  DB 입력
                </button>
              </th>
              <th>단위</th>
              <th>수량</th>
              <th>단가</th>
              <th>금액(자동)</th>
              <th>공급가액(자동)</th>
              <th>부가세(자동)</th>
              <th>비고</th>
              <th>추가</th>
            </tr>
          </thead>
          <tbody>
            {/* 입력행 */}
            <tr className={styles.inputRow}>
              <td className={styles.nameSuggestWrap} ref={nameSuggestRef}>
                <input
                  ref={nameInputRef}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  onFocus={() => form.name.trim() && setShowNameSuggest(true)}
                  onKeyDown={handleNameKeyDown}
                  autoComplete="off"
                />
                {showNameSuggest && filteredNameCodes.length > 0 && dropdownRect && createPortal(
                  <ul
                    className={styles.nameDropdown}
                    style={{ position: "fixed", top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width, marginTop: 2 }}
                  >
                    {filteredNameCodes.map((c, i) => (
                      <li
                        key={c.id}
                        className={`${styles.nameSuggestItem} ${i === nameHighlight ? styles.activeItem : ""}`}
                        onMouseDown={() => selectNameSuggestion(c)}
                      >
                        {c.name}
                      </li>
                    ))}
                  </ul>,
                  document.body
                )}
              </td>
              <td><input name="unit" value={form.unit} onChange={handleChange} /></td>
              <td><input name="qty" value={form.qty} onChange={handleChange} placeholder="예: 41.44" /></td>
              <td><input name="price" value={form.price} onChange={handleChange} /></td>
              <td>
                {/* ✅ 자동계산(회색)/수동 보정(흰색) 구분 — CLAUDE.md 자동생성 필드 규칙 */}
                <input
                  name="amount"
                  value={form.amount}
                  onChange={handleChange}
                  style={{ background: amountTouched ? "#fff" : "#f3f4f6" }}
                  title={amountTouched ? "수동 보정됨" : "수량×단가 자동계산(직접 수정 가능)"}
                />
              </td>
              <td><input name="supply" value={form.supply} onChange={handleChange} /></td>
              <td><input name="vat" value={form.vat} onChange={handleChange} /></td>
              <td><input name="remark" value={form.remark} onChange={handleChange} onKeyDown={handleKeyDown} /></td>
              <td><button onClick={addItem}>추가</button></td>
            </tr>

            {/* 데이터 행들 — 단위/수량/단가는 인라인 편집 가능(유류비(세차비) 기본
                2행처럼 빈 수량·단가를 사용자가 직접 채워야 하는 경우 대응). 공급가액/
                부가세도 인라인 편집 가능(수동 보정 시 흰 배경, updateItemField가
                한쪽만 고치면 나머지를 자동 보완). 금액≠공급가액+부가세면 금액 셀
                빨간 테두리 + 행 아래 안내 문구(차단 아님, 저장 시 별도 confirm). */}
            {items.map((item, idx) => {
              const supplyPlusVat = Number(item.supply || 0) + Number(item.vat || 0);
              const mismatch = Number(item.total || 0) !== supplyPlusVat;
              return (
                <React.Fragment key={idx}>
                  <tr>
                    <td>{item.name}</td>
                    <td>
                      <input
                        value={item.unit}
                        onChange={(e) => updateItemField(idx, "unit", e.target.value)}
                        style={INLINE_EDIT_STYLE}
                      />
                    </td>
                    <td>
                      <input
                        value={comma(item.qty)}
                        onChange={(e) => updateItemField(idx, "qty", e.target.value)}
                        placeholder="예: 41.44"
                        style={INLINE_EDIT_STYLE}
                      />
                    </td>
                    <td>
                      <input
                        value={comma(item.price)}
                        onChange={(e) => updateItemField(idx, "price", e.target.value)}
                        style={INLINE_EDIT_STYLE}
                      />
                    </td>
                    <td>
                      <input
                        value={comma(item.total)}
                        readOnly
                        style={{
                          ...INLINE_EDIT_STYLE,
                          background: "#f3f4f6",
                          border: mismatch ? "1px solid #dc2626" : INLINE_EDIT_STYLE.border,
                        }}
                        title={mismatch ? `금액(${comma(item.total)})과 공급가액+부가세(${comma(supplyPlusVat)})가 다릅니다` : "수량×단가 자동계산"}
                      />
                    </td>
                    <td>
                      <input
                        value={comma(item.supply)}
                        onChange={(e) => updateItemField(idx, "supply", e.target.value)}
                        style={{ ...INLINE_EDIT_STYLE, background: item.supplyTouched ? "#fff" : "#f3f4f6" }}
                        title={item.supplyTouched ? "수동 보정됨" : "자동계산(직접 수정 가능)"}
                      />
                    </td>
                    <td>
                      <input
                        value={comma(item.vat)}
                        onChange={(e) => updateItemField(idx, "vat", e.target.value)}
                        style={{ ...INLINE_EDIT_STYLE, background: item.vatTouched ? "#fff" : "#f3f4f6" }}
                        title={item.vatTouched ? "수동 보정됨" : "자동계산(직접 수정 가능)"}
                      />
                    </td>
                    <td>{item.remark}</td>
                    <td><button onClick={() => removeItem(idx)}>삭제</button></td>
                  </tr>
                  {mismatch && (
                    <tr>
                      <td colSpan={9} style={{ color: "#dc2626", fontSize: 12, padding: "2px 8px", textAlign: "left" }}>
                        ⚠️ 금액({comma(item.total)})과 공급가액+부가세({comma(supplyPlusVat)})가 {comma(Math.abs(Number(item.total || 0) - supplyPlusVat))}원 차이납니다
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* 합계 행 */}
            <tr className={styles.sumRow}>
              <td>합계</td>
              <td></td><td></td><td></td>
              <td>{comma(sumTotal)}</td>
              <td>{comma(sumSupply)}</td>
              <td>{comma(sumVat)}</td>
              <td></td><td></td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  );
}
